import { useEffect } from 'react'
import { Typography, Stack } from '@mui/material'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'

import BaseDialog from '@common/components/BaseDialog'
import { Dropdown } from '@common/components/Form/Dropdown'
import { SPACING } from '@common/theme/spacing'

const UpdateModalitySchema = z.object({
  newModality: z.string().min(1, 'Please select a modality.'),
})

type UpdateModalityFormValues = z.infer<typeof UpdateModalitySchema>

interface UpdateModalityModalProps {
  open: boolean
  onClose: () => void
  currentModality: string
  modalityAllowed: string[]
  onUpdateModality: (newModality: string) => void
}

export default function UpdateModalityModal({
  open,
  onClose,
  currentModality,
  modalityAllowed,
  onUpdateModality,
}: UpdateModalityModalProps) {
  const form = useForm<UpdateModalityFormValues>({
    resolver: zodResolver(UpdateModalitySchema),
    mode: 'onBlur',
    defaultValues: {
      // Initialize newModality with currentModality, but ensure it's one of the allowed ones if possible
      // Or, if currentModality is not allowed, default to the first allowed one or an empty string
      newModality: modalityAllowed.includes(currentModality)
        ? currentModality
        : modalityAllowed.length > 0
          ? modalityAllowed[0]
          : '',
    },
  })

  const { reset } = form

  useEffect(() => {
    if (open) {
      const initialModality = modalityAllowed.includes(currentModality)
        ? currentModality
        : modalityAllowed.length > 0
          ? modalityAllowed[0]
          : ''
      reset({ newModality: initialModality })
    }
  }, [open, currentModality, modalityAllowed, reset])

  const handleFormSubmit = (data: UpdateModalityFormValues) => {
    onUpdateModality(data.newModality)
    onClose() // Close the modal after update
  }

  // Prepare options for the Dropdown component
  const dropdownOptions = modalityAllowed.map((mod) => ({
    label: mod,
    value: mod,
  }))

  return (
    <BaseDialog
      title="Update Modality"
      description={
        modalityAllowed.includes(currentModality)
          ? ''
          : 'The current modality is not allowed. Please select a valid modality from the list.'
      }
      open={open}
      onClose={onClose}
      onSubmit={form.handleSubmit(handleFormSubmit)}
      form={form}
      submitLabel="Update"
      cancelLabel="Cancel"
    >
      <Stack spacing={SPACING.spacingXl}>
        {' '}
        {/* Use Stack for consistent spacing */}
        <Typography variant="body1" color="text.primary">
          Current Modality:{' '}
          <Typography component="span" fontWeight="bold">
            {currentModality}
          </Typography>
        </Typography>
        <Dropdown name="newModality" control={form.control} options={dropdownOptions} label="New Modality" />
      </Stack>
    </BaseDialog>
  )
}
